import * as React from 'react'
import { Link } from 'react-router-dom'
import { plantHref } from '@atbash-labs/plantview-links'
import type { PlantMesEntityRef, PlantMesUsageRefs } from '@axilon-apps/domains-ontology-ui-state'
import { SectionHeader } from '../entity/SectionHeader'
import { SessionEntityMark } from '../session-mark-ui'

type MesEntityLabel = 'Recipe' | 'Phase' | 'Batch' | 'Operation'

export function MesUsageSection({
  mesUsage
}: {
  mesUsage: PlantMesUsageRefs
}): React.ReactElement | null {
  const groups = (
    [
      { label: 'Recipes', nodeLabel: 'Recipe', entities: mesUsage.recipes },
      { label: 'Phases', nodeLabel: 'Phase', entities: mesUsage.phases },
      { label: 'Batches', nodeLabel: 'Batch', entities: mesUsage.batches },
      { label: 'Operations', nodeLabel: 'Operation', entities: mesUsage.operations }
    ] satisfies Array<{
      label: string
      nodeLabel: MesEntityLabel
      entities: PlantMesEntityRef[]
    }>
  ).filter((group) => group.entities.length > 0)

  if (groups.length === 0) return null
  const total = groups.reduce((sum, group) => sum + group.entities.length, 0)

  return (
    <section>
      <SectionHeader label="MES usage" subtitle={`${total}`} />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="mb-1.5 text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {group.label}
              </div>
              <div className="flex flex-col gap-1.5">
                {group.entities.map((entity) => (
                  <MesRow key={entity.id} entity={entity} label={group.nodeLabel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function MesRow({
  entity,
  label
}: {
  entity: PlantMesEntityRef
  label: MesEntityLabel
}): React.ReactElement {
  const href = plantHref(entity.id, label, entity)
  const title = <span className="text-13 font-medium text-ink">{entity.name}</span>

  return (
    <div className="flex min-w-0 items-center gap-2">
      {href ? (
        <Link
          to={href}
          className="min-w-0 flex-1 truncate text-ink no-underline hover:text-brand hover:underline"
          title={entity.id}
        >
          {title}
        </Link>
      ) : (
        <span className="min-w-0 flex-1 truncate">{title}</span>
      )}
      <SessionEntityMark entityId={entity.id} />
    </div>
  )
}
